import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

import { ApiError, api } from "../api/client";
import { Badge, Button, Avatar, Card, CardTitle, EmptyState, PageHeader } from "../components/ui";
import { PasswordInput } from "../components/PasswordInput";
import { useAuth } from "../auth/AuthProvider";
import { isAccountAdmin } from "../lib/people";
import { humanizeEnum } from "../lib/format";
import type {
  EmployeeFull,
  EmployeeLoginRole,
  EmployeePasswordResetWrite,
  EmployeeWrite,
  EmployeeWriteResult,
  Skill,
} from "../api/types";

const EMPLOYMENT_TYPES = [
  "full_time",
  "part_time",
  "intern",
  "trainee",
  "contract",
  "freelancer",
  "temporary",
  "consultant",
];
const STATUSES = ["active", "notice_period", "on_leave", "exited"];
const GENDERS = ["", "male", "female", "other", "undisclosed"];
const MARITAL_STATUSES = ["", "single", "married", "divorced", "widowed", "other", "undisclosed"];
const LOGIN_ROLES: Array<{ value: EmployeeLoginRole; label: string }> = [
  { value: "staff", label: "Employee" },
  { value: "manager", label: "Manager" },
];

/** Client mirror of the server's photo limits, for a fast local error. */
const ALLOWED_PHOTO_TYPES = ["image/png", "image/jpeg", "image/jpg", "image/webp", "image/gif"];
const MAX_PHOTO_BYTES = 2 * 1024 * 1024;

/** An error message for an unusable photo, or null when it is acceptable. */
export function validatePhotoFile(file: File): string | null {
  if (!ALLOWED_PHOTO_TYPES.includes(file.type)) return "Use a PNG, JPG, WEBP or GIF image.";
  if (file.size > MAX_PHOTO_BYTES) return "The image must be under 2 MB.";
  return null;
}

/** What the form wants done with the photo, alongside the saved record. */
interface PhotoIntent {
  file?: File;
  remove?: boolean;
}

/**
 * Server-required fields, mirrored on the client so a submit is checked before
 * the request and the failure explains itself. `joining_date` is required by
 * the API (the model has no default) -- it is the one field the old form left
 * unmarked, so an otherwise-complete record failed with a generic message.
 */
const REQUIRED_FIELDS: Array<{ key: string; label: string }> = [
  { key: "first_name", label: "First name" },
  { key: "last_name", label: "Last name" },
  { key: "joining_date", label: "Joining date" },
];

/** API field name -> the label shown on the form, for surfacing field errors. */
const FIELD_LABELS: Record<string, string> = {
  first_name: "First name",
  last_name: "Last name",
  joining_date: "Joining date",
  email: "Work email",
  phone: "Phone",
  date_of_birth: "Date of birth",
  designation: "Designation",
  department: "Department",
  branch: "Branch",
  employment_type: "Employment type",
  status: "Status",
  reporting_to: "Reports to",
  notice_period_days: "Notice period",
  login_email: "Login email",
  password: "Login password",
  new_password: "New password",
  confirm_new_password: "Confirm new password",
  role: "Role",
};

/**
 * "full_time" -> "Full Time", "notice_period" -> "Notice Period". The wire
 * value stays canonical; only the label is humanised, so filtering and the API
 * never depend on the display string.
 */
function humanize(value: string): string {
  return humanizeEnum(value);
}

/** The labels of server-required fields the form still needs filled in. */export function missingRequiredFields(
  values: Record<string, string | boolean>,
): string[] {
  return REQUIRED_FIELDS.filter(
    ({ key }) => !String(values[key] ?? "").trim(),
  ).map((field) => field.label);
}

/**
 * A pragmatic email shape check for instant feedback; the server stays the
 * authority on what a valid, available address is.
 */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Client-side validation for a requested login, or null when it is valid. The
 * server validates again and owns the truth; checking here turns the common
 * mistake (no address, malformed address, weak password) into instant feedback
 * instead of a round-trip error. An employee may be saved without a login, so
 * the login fields are validated only when they are actually requested. Once a
 * login is requested the owner must supply its password: there is no
 * invitation-link fallback in this flow.
 */
export function loginRequestError(
  values: Record<string, string | boolean>,
  requireLogin = false,
): string | null {
  const password = String(values.password ?? "");
  const creatingLogin = Boolean(values.create_login);
  if (!requireLogin && !creatingLogin && !password) return null;
  const email =
    String(values.login_email ?? "").trim() || String(values.email ?? "").trim();
  if (!email) return "Enter a work email or a login email to create the login.";
  if (!EMAIL_PATTERN.test(email)) return "Enter a valid email address for the login.";
  if (!password) return "Enter a login password.";
  if (password.length < 10 || /^\d+$/.test(password) || /^\p{L}+$/u.test(password)) {
    return "Use a mix of letters and numbers, at least 10 characters.";
  }
  if ((requireLogin || creatingLogin) && !["manager", "staff"].includes(String(values.role ?? ""))) {
    return "Choose Manager or Employee for this login.";
  }
  return null;
}

/**
 * Client-side validation for the owner-initiated password reset, or null when
 * the pair is usable. Mirrors the server's length and letter/number rule so the
 * common mistake is caught before the request.
 */
export function passwordResetError(
  newPassword: string,
  confirmPassword: string,
): string | null {
  if (!newPassword) return "Enter a new password.";
  if (
    newPassword.length < 10 ||
    /^\d+$/.test(newPassword) ||
    /^\p{L}+$/u.test(newPassword)
  ) {
    return "Use a mix of letters and numbers, at least 10 characters.";
  }
  if (!confirmPassword) return "Confirm the new password.";
  if (newPassword !== confirmPassword) return "The passwords do not match.";
  return null;
}

/**
 * The post-save notice for the login step, or null when no login was involved.
 * Pure so the success and failure wording is unit-testable without a DOM. The
 * console-backend case says plainly that no email actually left the server,
 * because "invitation sent" would otherwise promise an inbox that stays empty.
 */
export function loginOutcomeNotice(
  created: Pick<
    EmployeeWriteResult,
    "email" | "login_error" | "invite_sent" | "invite_delivery"
  >,
): { tone: "warn" | "error"; message: string } | null {
  const who = created.email || "the employee";
  if (created.login_error) {
    return {
      tone: "error",
      message: `Employee saved. Login not created: ${created.login_error}`,
    };
  }
  if (created.invite_sent) {
    if (created.invite_delivery === "console") {
      return {
        tone: "warn",
        message: `Employee saved. An invitation for ${who} was generated, but this environment uses the console email backend: the invitation link was printed to the server console and was not delivered by email.`,
      };
    }
    return {
      tone: "warn",
      message: `Employee saved. An invitation email was sent to ${who}.`,
    };
  }
  return null;
}

function skillsToText(skills: unknown): string {
  if (!Array.isArray(skills)) return "";
  return skills
    .map((skill) =>
      typeof skill === "object" && skill && "name" in skill
        ? `${(skill as Skill).name}: ${(skill as Skill).level}`
        : "",
    )
    .filter(Boolean)
    .join("\n");
}

function textToSkills(value: string): Skill[] {
  return value
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [name, level] = line.split(":").map((part) => part.trim());
      return { name: name ?? line, level: level ?? "" };
    });
}

function listToText(value: unknown): string {
  return Array.isArray(value) ? value.filter((v) => typeof v === "string").join(", ") : "";
}

function textToList(value: string): string[] {
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

/**
 * Create or edit an employment record.
 *
 * One page, two modes. Salary is deliberately absent -- an employment record is
 * who someone is, not what they are paid. Lifecycle dates are shown so a
 * resignation can be recorded without a separate "exit" screen.
 */
export function EmployeeFormPage({ selfService = false }: { selfService?: boolean } = {}) {
  const { id } = useParams();
  const editing = selfService || Boolean(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [notice, setNotice] = useState<{
    id: string;
    message: string;
    password?: string;
    tone?: "warn" | "error";
  } | null>(null);

  const employee = useQuery({
    queryKey: selfService ? ["employee-me"] : ["employee", id],
    queryFn: ({ signal }) =>
      selfService ? api.employeeMe(signal) : api.employee(id!, signal),
    enabled: editing,
  });
  const departments = useQuery({
    queryKey: ["departments", 100],
    queryFn: ({ signal }) => api.departmentsPage({ page_size: 100 }, signal),
  });
  const branches = useQuery({
    queryKey: ["branches"],
    queryFn: ({ signal }) => api.branches(signal),
  });
  const peers = useQuery({
    queryKey: ["employee-options"],
    queryFn: ({ signal }) => api.employees(signal),
  });

  const save = useMutation({
    mutationFn: async ({ body }: { body: EmployeeWrite; photo: PhotoIntent }) => {
      if (selfService) return api.updateEmployeeMe(body);
      if (editing) return api.updateEmployee(id!, body);
      return api.createEmployee(body);
    },
    onSuccess: async (created, variables) => {
      void queryClient.invalidateQueries({ queryKey: ["employees"] });
      // The profile reads the same ["employee", id] key this form loaded, so it
      // must be stale before navigating back or the saved values are not shown.
      void queryClient.invalidateQueries({ queryKey: ["employee", created.id] });
      void queryClient.invalidateQueries({ queryKey: ["employee-me"] });
      // A "Reports To" edit changes the Organization chart's shape, so its
      // cache must not outlive the save.
      void queryClient.invalidateQueries({ queryKey: ["org-employees"] });

      // The employment record is saved. The photo is a separate, optional step:
      // if storage rejects it, say so rather than reporting the save as fully
      // successful.
      const { photo } = variables;
      if (photo.file || photo.remove) {
        try {
          if (photo.remove) await api.removeEmployeePhoto(created.id);
          if (photo.file) await api.uploadEmployeePhoto(created.id, photo.file);
          void queryClient.invalidateQueries({ queryKey: ["employee", created.id] });
        } catch (err) {
          setNotice({
            id: created.id,
            tone: "error",
            message: `Employee saved, but the photo could not be saved: ${err instanceof ApiError ? err.message : "please try again."
              }`,
          });
          return;
        }
      }

      // Login provisioning is a separate step and can fail on its own (no seat,
      // duplicate/unknown email, a failed link or delivery); report what
      // happened rather than pretending the save failed or that an email
      // reached an inbox when it did not. `loginOutcomeNotice` owns the wording,
      // including the console-backend case.
      const loginNotice = loginOutcomeNotice(created);
      if (loginNotice) {
        setNotice({ id: created.id, ...loginNotice });
        return;
      }
      navigate(`/people/employees/${created.id}`, {
        state: {
          notice: selfService
            ? "Profile updated."
            : editing
              ? "Employee changes saved."
              : "Employee created.",
        },
      });
    },
  });

  if (editing && employee.isPending) {
    return <PageHeader title="Loading…" subtitle="" />;
  }
  if (editing && employee.isError) {
    return (
      <EmptyState
        title="Could not load your profile"
        body={
          employee.error instanceof ApiError
            ? employee.error.message
            : "Please try again."
        }
      />
    );
  }

  const record = employee.data;

  return (
    <>
      <PageHeader
        title={
          selfService
            ? "Edit my profile"
            : editing
              ? `Edit ${record?.display_name ?? "employee"}`
              : "Add employee"
        }
        subtitle={
          selfService
            ? "Your contact details. Employment, pay and statutory data are managed by your employer."
            : editing
              ? "The employment record someone is paid against."
              : "Identity, role and contact details. Pay is set up under Salary."
        }
        actions={
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft size={15} /> Back
          </Button>
        }
      />
      {notice ? (
        <div
          role="status"
          className={`mb-4 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md px-3 py-2 text-sm ${notice.tone === "error"
              ? "bg-rose-50 text-rose-700"
              : "bg-amber-50 text-amber-800"
            }`}
        >
          <span>{notice.message}</span>
          <Link
            to={`/people/employees/${notice.id}`}
            className="font-medium underline hover:no-underline"
          >
            Open the employee
          </Link>
        </div>
      ) : null}
      {record && record.has_login && isAccountAdmin(user?.role) ? (
        <ResetPasswordCard employee={record} />
      ) : null}
      <EmployeeFormFields
        key={editing ? id : "new"}
        initial={record}
        departments={departments.data?.results ?? []}
        branches={branches.data ?? []}
        employees={peers.data ?? []}
        canIssueLogin={!selfService && isAccountAdmin(user?.role)}
        selfService={selfService}
        error={save.error instanceof ApiError ? save.error : null}
        pending={save.isPending}
        onCancel={() =>
          navigate(
            selfService
              ? "/people/me"
              : editing
                ? `/people/employees/${id}`
                : "/people/employees",
          )
        }
        onSubmit={(body, photo) => save.mutate({ body, photo })}
      />
    </>
  );
}

/**
 * Owner/admin credential reset for an existing login.
 *
 * Kept in its own form so Enter in these fields can never submit (and save) the
 * employment record. The success message is deliberately non-secret: the server
 * never returns the password, and the fields clear as soon as it is accepted.
 */
function ResetPasswordCard({ employee }: { employee: EmployeeFull }) {
  const queryClient = useQueryClient();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const reset = useMutation({
    mutationFn: (body: EmployeePasswordResetWrite) =>
      api.resetEmployeePassword(employee.id, body),
    onSuccess: (result) => {
      setNewPassword("");
      setConfirmPassword("");
      setLocalError(null);
      setDone(result.detail);
      void queryClient.invalidateQueries({ queryKey: ["employee", employee.id] });
      void queryClient.invalidateQueries({ queryKey: ["employees"] });
    },
  });

  const error = reset.error instanceof ApiError ? reset.error : null;
  const pendingInvite = employee.invite_status !== "accepted";

  return (
    <Card className="mb-5">
      <CardTitle action={<Badge tone="slate">Owner only</Badge>}>
        Login credentials
      </CardTitle>
      <p className="mb-3 text-sm text-slate-500">
        Set a new password for{" "}
        <span className="font-medium text-slate-700">
          {employee.email || "this login"}
        </span>
        . Their role and seat stay the same.
      </p>
      {pendingInvite ? (
        <p className="mb-3 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
          This login has not been activated yet. Setting a password activates it
          and cancels the outstanding invitation link.
        </p>
      ) : null}
      <form
        onSubmit={(event) => {
          event.preventDefault();
          const issue = passwordResetError(newPassword, confirmPassword);
          if (issue) {
            setDone(null);
            setLocalError(issue);
            return;
          }
          setLocalError(null);
          setDone(null);
          reset.mutate({
            new_password: newPassword,
            confirm_new_password: confirmPassword,
          });
        }}
        className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] sm:items-end"
      >
        <Field label="New password" required>
          <PasswordInput
            id="employee-reset-password"
            value={newPassword}
            autoComplete="new-password"
            required
            onChange={setNewPassword}
          />
        </Field>
        <Field label="Confirm new password" required>
          <PasswordInput
            id="employee-reset-confirm-password"
            value={confirmPassword}
            autoComplete="new-password"
            required
            onChange={setConfirmPassword}
          />
        </Field>
        <Button type="submit" variant="primary" disabled={reset.isPending}>
          {reset.isPending ? "Resetting…" : "Reset password"}
        </Button>
      </form>
      <p className="mt-2 text-xs text-slate-500">
        At least 10 characters, with letters and numbers.
      </p>
      {localError ? (
        <p role="alert" className="mt-3 rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {localError}
        </p>
      ) : null}
      {error ? (
        <div
          role="alert"
          className="mt-3 rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700"
        >
          <p>{error.message}</p>
          {Object.keys(error.fields).length ? (
            <ul className="mt-1 list-disc space-y-0.5 pl-5">
              {Object.entries(error.fields).map(([field, messages]) => (
                <li key={field}>
                  <span className="font-medium">
                    {FIELD_LABELS[field] ?? field}
                  </span>
                  {": "}
                  {messages.join(" ")}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
      {done ? (
        <p role="status" className="mt-3 rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          {done}
        </p>
      ) : null}
    </Card>
  );
}

/**
 * Build the API payload from form values. Kept separate from the submit handler
 * so the credential fields sent for a requested login are directly testable.
 */
export function employeeWriteBody(
  values: Record<string, string | boolean>,
  {
    selfService,
    showLoginFields,
  }: { selfService: boolean; showLoginFields: boolean },
): EmployeeWrite {
  const personalBody: EmployeeWrite = {
    first_name: String(values.first_name),
    last_name: String(values.last_name),
    email: String(values.email) || undefined,
    phone: String(values.phone) || undefined,
    gender: (String(values.gender) || "") as EmployeeWrite["gender"],
    date_of_birth: String(values.date_of_birth) || null,
    marital_status: String(values.marital_status) || undefined,
    address: String(values.address) || undefined,
    emergency_contact_name: String(values.emergency_contact_name) || undefined,
    emergency_contact_phone: String(values.emergency_contact_phone) || undefined,
  };
  if (selfService) return personalBody;

  return {
    ...personalBody,
    designation: String(values.designation) || undefined,
    department: String(values.department) || null,
    branch: String(values.branch) || null,
    employment_type: String(values.employment_type) || undefined,
    status: String(values.status),
    joining_date: String(values.joining_date) || undefined,
    probation_end_date: String(values.probation_end_date) || null,
    confirmation_date: String(values.confirmation_date) || null,
    notice_period_days: Number(values.notice_period_days) || 0,
    resignation_date: String(values.resignation_date) || null,
    last_working_day: String(values.last_working_day) || null,
    exit_date: String(values.exit_date) || null,
    exit_reason: String(values.exit_reason) || undefined,
    reporting_to: String(values.reporting_to) || null,
    is_provident_fund_applicable: Boolean(values.is_provident_fund_applicable),
    is_esic_applicable: Boolean(values.is_esic_applicable),
    bank_account_ref: String(values.bank_account_ref) || undefined,
    tax_identifier: String(values.tax_identifier) || undefined,
    skills: textToSkills(String(values.skills)),
    certifications: textToList(String(values.certifications)),
    tags: textToList(String(values.tags)),
    notes: String(values.notes) || undefined,
    ...(showLoginFields && Boolean(values.create_login)
      ? {
        login_email: String(values.login_email) || undefined,
        create_login: true,
        password: String(values.password),
        role: String(values.role) as EmployeeLoginRole,
      }
      : {}),
  };
}

function EmployeeFormFields({
  initial,
  departments,
  branches,
  employees,
  canIssueLogin,
  selfService,
  error,
  pending,
  onCancel,
  onSubmit,
}: {
  initial?: EmployeeFull;
  departments: Array<{ id: string; name: string }>;
  branches: Array<{ id: string; name: string; is_default?: boolean }>;
  employees: Array<{ id: string; display_name: string; is_account_owner?: boolean }>;
  canIssueLogin: boolean;
  selfService: boolean;
  error: ApiError | null;
  pending: boolean;
  onCancel: () => void;
  onSubmit: (body: EmployeeWrite, photo: PhotoIntent) => void;
}) {
  const [values, setValues] = useState<Record<string, string | boolean>>({
    first_name: initial?.first_name ?? "",
    last_name: initial?.last_name ?? "",
    email: initial?.email ?? "",
    phone: initial?.phone ?? "",
    gender: initial?.gender ?? "",
    date_of_birth: initial?.date_of_birth ?? "",
    marital_status: initial?.marital_status ?? "",
    address: initial?.address ?? "",
    photo_url: initial?.photo_url ?? "",
    designation: initial?.designation ?? "",
    department: initial?.department ?? "",
    branch: initial?.branch ?? "",
    employment_type: initial?.employment_type ?? "",
    status: initial?.status ?? "active",
    joining_date: initial?.joining_date ?? "",
    probation_end_date: initial?.probation_end_date ?? "",
    confirmation_date: initial?.confirmation_date ?? "",
    notice_period_days: String(initial?.notice_period_days ?? 0),
    resignation_date: initial?.resignation_date ?? "",
    last_working_day: initial?.last_working_day ?? "",
    exit_date: initial?.exit_date ?? "",
    exit_reason: initial?.exit_reason ?? "",
    reporting_to: initial?.reporting_to ?? "",
    is_provident_fund_applicable: initial?.is_provident_fund_applicable ?? false,
    is_esic_applicable: initial?.is_esic_applicable ?? false,
    emergency_contact_name: initial?.emergency_contact_name ?? "",
    emergency_contact_phone: initial?.emergency_contact_phone ?? "",
    bank_account_ref: initial?.bank_account_ref ?? "",
    tax_identifier: initial?.tax_identifier ?? "",
    skills: skillsToText(initial?.skills),
    certifications: listToText(initial?.certifications),
    tags: listToText(initial?.tags),
    login_email: "",
    create_login: false,
    password: "",
    role: initial?.user_role === "manager" ? "manager" : "staff",
    notes: initial?.notes ?? "",
  });

  // Mirror the server's create-time defaults so the owner sees what will be
  // saved: a new hire reports to the account owner and lands in the default
  // branch unless a manager or branch is chosen. Each default is applied once,
  // when its reference list arrives, and never overwrites a choice already made.
  const ownerDefaulted = useRef(false);
  const branchDefaulted = useRef(false);
  const defaultOwnerId = employees.find((e) => e.is_account_owner)?.id ?? "";
  const defaultBranchId = branches.find((b) => b.is_default)?.id ?? "";
  useEffect(() => {
    if (initial) return;
    if (!ownerDefaulted.current && defaultOwnerId) {
      ownerDefaulted.current = true;
      setValues((prev) =>
        prev.reporting_to ? prev : { ...prev, reporting_to: defaultOwnerId },
      );
    }
    if (!branchDefaulted.current && defaultBranchId) {
      branchDefaulted.current = true;
      setValues((prev) =>
        prev.branch ? prev : { ...prev, branch: defaultBranchId },
      );
    }
  }, [initial, defaultOwnerId, defaultBranchId]);

  const set = (key: string, value: string | boolean) =>
    setValues((prev) => ({ ...prev, [key]: value }));
  const toggle = (key: string) => set(key, !Boolean(values[key]));

  // The API's field-level errors, keyed by field name. Rendered below with the
  // same labels the form uses, so "joining_date: This field is required."
  // reads as "Joining date: This field is required."
  const fieldErrors = error?.fields ?? {};
  const [localError, setLocalError] = useState<string | null>(null);

  // The photo is uploaded only after the record exists (its id is needed), so
  // the form carries the intent and the parent performs it in `onSuccess`.
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [removePhoto, setRemovePhoto] = useState(false);
  const [photoError, setPhotoError] = useState<string | null>(null);

  // The login step applies when creating a hire, or when attaching a first
  // login to an existing employee who has none. An employee who already has a
  // login manages it from their record (invite/role actions), so this form
  // must never silently re-provision or reset an existing account.
  const showLoginFields = canIssueLogin && (!initial || !initial.has_login);

  return (
    <Card>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          // Check the server-required fields before sending, and say which
          // ones are missing rather than relying on a generic 400.
          const missing = missingRequiredFields(values);
          if (missing.length) {
            setLocalError(`Please complete: ${missing.join(", ")}.`);
            return;
          }
          if (showLoginFields && Boolean(values.create_login)) {
            const loginIssue = loginRequestError(values, true);
            if (loginIssue) {
              setLocalError(loginIssue);
              return;
            }
          }
          setLocalError(null);
          const body = employeeWriteBody(values, { selfService, showLoginFields });
          onSubmit(body, { file: photoFile ?? undefined, remove: removePhoto });
        }}
        className="grid gap-5"
      >
        <Fieldset title="Personal">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="First name" required>
              <TextInput value={String(values.first_name)} onChange={(v) => set("first_name", v)} required />
            </Field>
            <Field label="Last name" required>
              <TextInput value={String(values.last_name)} onChange={(v) => set("last_name", v)} required />
            </Field>
            <Field label={selfService ? "Personal email" : "Work email"}>
              <TextInput type="email" value={String(values.email)} onChange={(v) => set("email", v)} />
            </Field>
            <Field label={selfService ? "Personal phone" : "Phone"}>
              <TextInput value={String(values.phone)} onChange={(v) => set("phone", v)} />
            </Field>
            <Field label="Gender">
              <Select
                value={String(values.gender)}
                onChange={(v) => set("gender", v)}
                options={GENDERS.map((g) => ({ value: g, label: g ? humanize(g) : "Not specified" }))}
              />
            </Field>
            <Field label="Date of birth">
              <TextInput type="date" value={String(values.date_of_birth)} onChange={(v) => set("date_of_birth", v)} />
            </Field>
            <Field label="Marital status">
              <Select
                value={String(values.marital_status)}
                onChange={(v) => set("marital_status", v)}
                options={MARITAL_STATUSES.map((status) => ({
                  value: status,
                  label: status ? humanize(status) : "Not specified",
                }))}
              />
            </Field>
            <Field label="Emergency contact name">
              <TextInput
                value={String(values.emergency_contact_name)}
                onChange={(v) => set("emergency_contact_name", v)}
              />
            </Field>
            <Field label="Emergency contact phone">
              <TextInput
                value={String(values.emergency_contact_phone)}
                onChange={(v) => set("emergency_contact_phone", v)}
              />
            </Field>
          </div>
          <Field label="Address">
            <textarea
              value={String(values.address)}
              onChange={(event) => set("address", event.target.value)}
              rows={2}
              className={textareaClass}
            />
          </Field>
          <Field label="Photo">
            <PhotoField
              name={initial?.display_name ?? "Employee"}
              current={initial?.photo ?? ""}
              file={photoFile}
              remove={removePhoto}
              error={photoError}
              onChange={(file, err) => {
                setPhotoFile(file);
                setPhotoError(err);
                if (file) setRemovePhoto(false);
              }}
              onRemove={() => {
                setPhotoFile(null);
                setPhotoError(null);
                setRemovePhoto(true);
              }}
            />
          </Field>
        </Fieldset>

        {selfService ? (
          <p className="rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-600">
            Employment, pay, bank, statutory, role and reporting details are
            managed by your employer.
          </p>
        ) : null}

        {!selfService ? (
          <>
            <Fieldset title="Employment">
              <div className="grid gap-4 sm:grid-cols-3">
                <Field label="Designation">
                  <TextInput value={String(values.designation)} onChange={(v) => set("designation", v)} />
                </Field>
                <Field label="Department">
                  <Select
                    value={String(values.department)}
                    onChange={(v) => set("department", v)}
                    options={[
                      { value: "", label: "No department" },
                      ...departments.map((d) => ({ value: d.id, label: d.name })),
                    ]}
                  />
                  {departments.length === 0 ? (
                    <p className="mt-1 text-xs text-slate-500">
                      No departments configured.{" "}
                      <Link to="/people/organization" className="underline">
                        Add one under Organisation
                      </Link>
                      .
                    </p>
                  ) : null}
                </Field>
                <Field label="Branch">
                  <Select
                    value={String(values.branch)}
                    onChange={(v) => set("branch", v)}
                    options={[
                      { value: "", label: "No branch" },
                      ...branches.map((b) => ({ value: b.id, label: b.name })),
                    ]}
                  />
                  {branches.length === 0 ? (
                    <p className="mt-1 text-xs text-slate-500">
                      No work locations configured.{" "}
                      <Link to="/people/organization" className="underline">
                        Add one under Organisation
                      </Link>
                      .
                    </p>
                  ) : null}
                </Field>
                <Field label="Employment type">
                  <Select
                    value={String(values.employment_type)}
                    onChange={(v) => set("employment_type", v)}
                    options={[
                      { value: "", label: "Not set" },
                      ...EMPLOYMENT_TYPES.map((t) => ({ value: t, label: humanize(t) })),
                    ]}
                  />
                </Field>
                <Field label="Status">
                  <Select
                    value={String(values.status)}
                    onChange={(v) => set("status", v)}
                    options={STATUSES.map((s) => ({ value: s, label: humanize(s) }))}
                  />
                </Field>
                <Field label="Reports to">
                  <Select
                    value={String(values.reporting_to)}
                    onChange={(v) => set("reporting_to", v)}
                    options={[
                      { value: "", label: "No manager" },
                      ...employees
                        .filter((e) => e.id !== initial?.id)
                        .map((e) => ({ value: e.id, label: e.display_name })),
                    ]}
                  />
                </Field>
                <Field label="Joining date" required>
                  <TextInput
                    type="date"
                    value={String(values.joining_date)}
                    onChange={(v) => set("joining_date", v)}
                    required
                  />
                </Field>
                <Field label="Probation end">
                  <TextInput
                    type="date"
                    value={String(values.probation_end_date)}
                    onChange={(v) => set("probation_end_date", v)}
                  />
                </Field>
                <Field label="Confirmation date">
                  <TextInput
                    type="date"
                    value={String(values.confirmation_date)}
                    onChange={(v) => set("confirmation_date", v)}
                  />
                </Field>
                <Field label="Notice period (days)">
                  <TextInput
                    type="number"
                    value={String(values.notice_period_days)}
                    onChange={(v) => set("notice_period_days", v)}
                  />
                </Field>
              </div>
            </Fieldset>

            <Fieldset title="Lifecycle">
              <div className="grid gap-4 sm:grid-cols-3">
                <Field label="Resignation date">
                  <TextInput
                    type="date"
                    value={String(values.resignation_date)}
                    onChange={(v) => set("resignation_date", v)}
                  />
                </Field>
                <Field label="Last working day">
                  <TextInput
                    type="date"
                    value={String(values.last_working_day)}
                    onChange={(v) => set("last_working_day", v)}
                  />
                </Field>
                <Field label="Exit date">
                  <TextInput type="date" value={String(values.exit_date)} onChange={(v) => set("exit_date", v)} />
                </Field>
              </div>
              <Field label="Exit reason">
                <TextInput value={String(values.exit_reason)} onChange={(v) => set("exit_reason", v)} />
              </Field>
              <p className="text-xs text-slate-500">
                Last working day is the employee's final working day; exit date is the
                administrative closure. Deactivating keeps all history.
              </p>
            </Fieldset>

            <Fieldset title="Professional">
              <Field label="Skills (one per line, “Name: Level”)">
                <textarea
                  value={String(values.skills)}
                  onChange={(event) => set("skills", event.target.value)}
                  rows={3}
                  placeholder={"Hair Cutting: Expert\nHair Colour: Advanced"}
                  className={textareaClass}
                />
              </Field>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Certifications (comma separated)">
                  <TextInput value={String(values.certifications)} onChange={(v) => set("certifications", v)} />
                </Field>
                <Field label="Tags (comma separated)">
                  <TextInput value={String(values.tags)} onChange={(v) => set("tags", v)} />
                </Field>
              </div>
            </Fieldset>

            <Fieldset title="Pay & compliance">
              <div className="grid gap-3 sm:grid-cols-2">
                <Checkbox
                  label="Provident fund applicable"
                  checked={Boolean(values.is_provident_fund_applicable)}
                  onChange={() => toggle("is_provident_fund_applicable")}
                />
                <Checkbox
                  label="ESIC applicable"
                  checked={Boolean(values.is_esic_applicable)}
                  onChange={() => toggle("is_esic_applicable")}
                />
                <Field label="Bank account (last digits / ref)">
                  <TextInput value={String(values.bank_account_ref)} onChange={(v) => set("bank_account_ref", v)} />
                </Field>
                <Field label="Tax identifier">
                  <TextInput value={String(values.tax_identifier)} onChange={(v) => set("tax_identifier", v)} />
                </Field>
              </div>
            </Fieldset>

            {showLoginFields ? (
              <Fieldset title="Login & Roles">
                <Checkbox
                  label="Create a login for this employee (uses a seat)"
                  checked={Boolean(values.create_login)}
                  onChange={() => toggle("create_login")}
                />
                {Boolean(values.create_login) ? (
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="Login email">
                      <TextInput
                        type="email"
                        value={String(values.login_email)}
                        onChange={(v) => set("login_email", v)}
                        placeholder="Defaults to the work email"
                      />
                      <span className="mt-1 block text-xs text-slate-400">
                        The address this person will use to sign in.
                      </span>
                    </Field>
                    <Field label="Role" required>
                      <Select
                        value={String(values.role)}
                        onChange={(v) => set("role", v)}
                        options={LOGIN_ROLES}
                      />
                      <span className="mt-1 block text-xs text-slate-400">
                        Managers see their team; employees see their own work.
                      </span>
                    </Field>
                    <Field label="Login password" required>
                      <PasswordInput
                        id="employee-password"
                        value={String(values.password)}
                        autoComplete="new-password"
                        required
                        onChange={(value) => set("password", value)}
                      />
                      <span className="mt-1 block text-xs text-slate-400">
                        At least 10 characters, with letters and numbers. The
                        employee can change it after signing in.
                      </span>
                    </Field>
                  </div>
                ) : (
                  <p className="text-xs text-slate-500">
                    Saved as an employee record only. No login is created and no
                    seat is used; you can add a login later from the employee's
                    record.
                  </p>
                )}
              </Fieldset>
            ) : null}

            <Fieldset title="Notes">
              <textarea
                value={String(values.notes)}
                onChange={(event) => set("notes", event.target.value)}
                rows={3}
                className={textareaClass}
              />
            </Fieldset>
          </>
        ) : null}

        {localError ? (
          <p role="alert" className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {localError}
          </p>
        ) : null}

        {error ? (
          <div
            role="alert"
            className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700"
          >
            <p>{error.message}</p>
            {Object.keys(fieldErrors).length ? (
              <ul className="mt-1 list-disc space-y-0.5 pl-5">
                {Object.entries(fieldErrors).map(([field, messages]) => (
                  <li key={field}>
                    <span className="font-medium">{FIELD_LABELS[field] ?? field}</span>
                    {": "}
                    {messages.join(" ")}
                  </li>
                ))}
              </ul>
            ) : null}
            {error.isPlanLimit ? (
              <p className="mt-1">
                Upgrade your plan, then add the employee again.
              </p>
            ) : null}
          </div>
        ) : null}

        <div className="flex justify-end gap-2 border-t border-slate-100 pt-4">
          <Button onClick={onCancel}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={pending}>
            {pending ? "Saving…" : initial ? "Save changes" : "Add employee"}
          </Button>
        </div>
      </form>
    </Card>
  );
}

function Fieldset({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <fieldset className="grid gap-3">
      <legend className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
        {title}
      </legend>
      {children}
    </fieldset>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label className="block text-sm">
      <span className="mb-1 block text-xs font-medium text-slate-600">
        {label}
        {required ? <span className="text-rose-500"> *</span> : null}
      </span>
      {children}
    </label>
  );
}

const inputClass =
  "w-full rounded-md border-0 px-3 py-2 text-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-slate-900";
const textareaClass = inputClass;

function TextInput({
  value,
  onChange,
  type = "text",
  required,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  type?: string;
  required?: boolean;
  placeholder?: string;
}) {
  return (
    <input
      type={type}
      value={value}
      required={required}
      placeholder={placeholder}
      onChange={(event) => onChange(event.target.value)}
      className={inputClass}
    />
  );
}

function Select({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <select value={value} onChange={(event) => onChange(event.target.value)} className={inputClass}>
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

/**
 * Photo upload with a local preview. The file is validated here for a fast
 * error, and again on the server (the authority). The record is not touched
 * until the parent uploads the chosen file after the save.
 */
function PhotoField({
  name,
  current,
  file,
  remove,
  error,
  onChange,
  onRemove,
}: {
  name: string;
  current: string;
  file: File | null;
  remove: boolean;
  error: string | null;
  onChange: (file: File | null, error: string | null) => void;
  onRemove: () => void;
}) {
  const [preview, setPreview] = useState<string | null>(null);

  useEffect(() => {
    if (!file) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const shown = preview ?? (remove ? "" : current);

  function handleFile(event: React.ChangeEvent<HTMLInputElement>) {
    const selected = event.target.files?.[0] ?? null;
    event.target.value = ""; // allow re-selecting the same file
    if (!selected) {
      onChange(null, null);
      return;
    }
    const err = validatePhotoFile(selected);
    if (err) {
      onChange(null, err);
      return;
    }
    onChange(selected, null);
  }

  return (
    <div className="flex items-center gap-3">
      <Avatar name={name} src={shown} size={56} />
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <label className="inline-flex cursor-pointer items-center rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50">
            {shown ? "Replace photo" : "Upload photo"}
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              onChange={handleFile}
              className="sr-only"
            />
          </label>
          {shown ? (
            <Button type="button" variant="ghost" onClick={onRemove}>
              Remove
            </Button>
          ) : null}
        </div>
        <p className="mt-1 text-xs text-slate-500">PNG, JPG, WEBP or GIF, up to 2 MB.</p>
        {error ? <p className="mt-1 text-xs text-rose-600">{error}</p> : null}
      </div>
    </div>
  );
}

function Checkbox({ label, checked, onChange }: { label: string; checked: boolean; onChange: () => void }) {
  return (
    <label className="flex items-center gap-2 text-sm text-slate-700">
      <input type="checkbox" checked={checked} onChange={onChange} className="size-4 accent-slate-900" />
      {label}
    </label>
  );
}
